/**
 * Owner: Masa Bayu
 * File: PrivateAuthorRail.tsx
 * Date:Time: 2026-10-23 09:00
 * Description: Rail kanan dashboard PRIVAT §467-C — Pintasan Pantas (6 jubai,
 *   setiap sasaran laluan nyata / URL qxeditor), Notifikasi = HANYA fakta
 *   pelayan yang menunggu tindakan (pesanan menunggu bayaran, terbitan tanpa
 *   harga, pesanan berbayar) dengan kiraan benar, dan kad promo → storefront.
 *   Notifikasi demo 4 item + masa karangan dan lencana pencapaian dibuang.
 */

import type { ReactElement, ReactNode } from 'react';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import { ALERT_ACTIONS, AUTHOR_SHORTCUTS, alertText } from './private-author-view';
import type { AuthorDashboardModel } from './private-author-view';
import type { PrivateAuthorLabels } from './private-author-labels';

const SHORTCUT_TONES = ['#e8f1ff', '#eaf8ef', '#fff4e6', '#f3e8ff', '#fdeef1', '#e6f7fb'];

function Stroke({
  children,
  size = 18,
}: {
  readonly children: ReactNode;
  readonly size?: number;
}): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const SHORTCUT_ICONS: Record<string, ReactElement> = {
  upload: (
    <Stroke>
      <path d="M12 16V4" />
      <path d="M7 9l5-5 5 5" />
      <path d="M4 20h16" />
    </Stroke>
  ),
  bookPlus: (
    <Stroke>
      <path d="M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2z" />
      <path d="M14 8v6M11 11h6" />
    </Stroke>
  ),
  megaphone: (
    <Stroke>
      <path d="M4 11l12-6v14L4 13z" />
      <path d="M4 11v3M18 8a3 3 0 0 1 0 6" />
    </Stroke>
  ),
  report: (
    <Stroke>
      <path d="M5 21V9l5-5 9 3v13z" />
      <path d="M9 13h6M9 17h6" />
    </Stroke>
  ),
  price: (
    <Stroke>
      <path d="M12 3v6" />
      <circle cx="12" cy="15" r="6" />
      <path d="M9.8 13.4c1.5-1.2 3.8-.6 4.4.8.7 1.6-1 2.4-2.2 2.4-1.3 0-2.4.8-2.2 2.1.2 1.2 1.9 1.7 3.4 1" />
    </Stroke>
  ),
  receipt: (
    <Stroke>
      <path d="M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5L6 21z" />
      <path d="M9.5 9h5M9.5 13h5" />
    </Stroke>
  ),
};

export interface PrivateAuthorRailProps {
  readonly onNavigate: (path: string) => void;
  readonly la: PrivateAuthorLabels;
  readonly model: AuthorDashboardModel;
}

export function PrivateAuthorRail({ onNavigate, la, model }: PrivateAuthorRailProps): ReactElement {
  return (
    <aside className="qxp__rail" aria-label={la.shortcutsTitle}>
      <section className="qxp__card" aria-label={la.shortcutsTitle}>
        <h2 className="qxp__card-title">{la.shortcutsTitle}</h2>
        <div className="qxp__shortcuts">
          {AUTHOR_SHORTCUTS.map((s, i) => (
            <button
              key={s.id}
              type="button"
              className="qxp__shortcut qx-motion-press"
              style={{ background: SHORTCUT_TONES[i % SHORTCUT_TONES.length] ?? '#e8f1ff' }}
              onClick={() => {
                onNavigate(s.target);
              }}
            >
              {SHORTCUT_ICONS[s.icon]}
              <span>{la.shortcutLabels[s.id]}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="qxp__card" id="qxp-notifications" aria-label={la.notificationsTitle}>
        <div className="qxp__card-head">
          <h2 className="qxp__card-title">{la.notificationsTitle}</h2>
        </div>
        {model.alerts.length === 0 ? (
          <p className="qxp__empty">{la.notificationsEmpty}</p>
        ) : (
          <ul className="qxp__notifs">
            {model.alerts.map((alert) => (
              <li key={alert.id}>
                <button
                  type="button"
                  className="qxp__notif"
                  onClick={() => {
                    onNavigate(ALERT_ACTIONS[alert.kind].target);
                  }}
                >
                  <span className="qxp__notif-ic" aria-hidden="true">
                    <LandingIcon name={ALERT_ACTIONS[alert.kind].icon} size={16} />
                  </span>
                  <span className="qxp__notif-text">{alertText(alert, la)}</span>
                  <LandingIcon name="chevronRight" size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="qxp__promo" aria-label={la.promoTitle}>
        <h2>{la.promoTitle}</h2>
        <p>{la.promoText}</p>
        <button
          type="button"
          className="qxp__promo-cta qx-motion-press"
          onClick={() => {
            onNavigate('/author/storefront');
          }}
        >
          {la.promoCta}
        </button>
      </section>
    </aside>
  );
}
